/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from "../../../components/button.component";
import { Card } from "../../../components/card.component";
import { useHistory } from "react-router-dom";
import { AppRouteApi, AppRouteUi } from "../../../config/appRoute";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppointmentList } from "../../../features/appointment/appointmentList.component";
import { useAppDispatch } from "../../../core/redux";
import { ActionsAppointmentTable } from "../../../core/redux/slices/sliceAppointmentTable";
import { useAppSelector } from "../../../core/redux/ReduxStore";
import { ExportButton } from "../../../components/export.component";
import { Form, Nav } from "rsuite";
import { SelectPickerInput } from "../../../components/textInput.component";
import { ApiPatient, ApiCaregiver } from "../../../core/apis";
import { SearchQueryParams } from "../../../config/constants";
import { EnumAppointmentStatus } from "../../../libs";
import { useTablePaginationSearchFilter } from "../../../hooks";
import { ApiSchemaFilteredAppointment } from "../../../models/api";
import { CustomDatePicker } from "../../../components";
import { VisitList } from "../../../features/visit/visitList.component";
import { StorageAuth } from "../../../core/localStorage";

export const PageAppointmentView = () => {
  const [activeTab, setActiveTab] = useState("appointments");
  const searchParam = new URLSearchParams(window.location.search);
  const fromDate = useMemo(
    () => searchParam.get(SearchQueryParams.FromDate),
    []
  );
  const toDate = useMemo(() => searchParam.get(SearchQueryParams.ToDate), []);
  const predefinedStatus = useMemo(
    () =>
      searchParam.get(
        SearchQueryParams.AppointmentStatus
      ) as EnumAppointmentStatus,
    []
  );
  const {
    pageNumber,
    pageSize,
    searchKey,
    nextPageToken,
    shouldRefetch,
    status,
    startDate,
    endDate,
    patientSKey,
    caregiverSKey,
  } = useAppSelector((state) => state.appointmentTable);

  // Get patient and caregiver data for filters
  const selectedCompany = StorageAuth.CompanyId || "";
  const { data: patients } = ApiPatient.GetAllFromCompany.Hook(selectedCompany);
  const { data: caregivers } =
    ApiCaregiver.GetAllFromCompany.Hook(selectedCompany);

  const apiParams = useMemo(() => {
    return {
      PageNumber: pageNumber,
      PageSize: pageSize,
      SearchKey: searchKey,
      NextPageToken: nextPageToken,
      AppointmentStatus: status || predefinedStatus,
      StartDate: startDate || fromDate,
      EndDate: endDate || toDate,
      PatientId: patientSKey || undefined,
      CaregiverId: caregiverSKey || undefined,
    };
  }, [
    pageNumber,
    pageSize,
    searchKey,
    nextPageToken,
    startDate,
    endDate,
    status,
    patientSKey,
    caregiverSKey,
    predefinedStatus,
    fromDate,
    toDate,
  ]);
  const {
    apiData: appointmentData,
    isFetching: isAppointmentDataFetching,
    refetch: refetchAppointment,
  } = useTablePaginationSearchFilter<
    ApiSchemaFilteredAppointment,
    typeof ActionsAppointmentTable
  >(ActionsAppointmentTable, {
    apiRoute: AppRouteApi.Appointment.Paginated(),
    requestType: "POST",
    customParams: apiParams,
  });

  const appointmentStatus: { label: string; value: EnumAppointmentStatus }[] = [
    {
      label: "NotAccepted",
      value: EnumAppointmentStatus.NotAccepted,
    },
    {
      label: "Upcoming",
      value: EnumAppointmentStatus.Upcoming,
    },
    {
      label: "Ongoing",
      value: EnumAppointmentStatus.Ongoing,
    },
    {
      label: "Completed",
      value: EnumAppointmentStatus.Complete,
    },
  ];

  // Format patient and caregiver data for SelectPickers
  const patientSelectItems = useMemo(() => {
    return (
      patients?.map((patient) => ({
        label: `${patient.firstName} ${patient.lastName}`,
        value: patient.sKey,
      })) || []
    );
  }, [patients]);

  const caregiverSelectItems = useMemo(() => {
    return (
      caregivers?.map((caregiver) => ({
        label: `${caregiver.firstName} ${caregiver.lastName}`,
        value: caregiver.sKey,
      })) || []
    );
  }, [caregivers]);

  const dispatch = useAppDispatch();
  const history = useHistory();

  const handleChange = () => {
    dispatch(ActionsAppointmentTable.goToPage(1));
  };

  const resetData = useCallback(() => {
    dispatch(ActionsAppointmentTable.resetData());
  }, [dispatch]);

  useEffect(() => {
    history.replace(AppRouteUi.Appointment.List());

    return () => {
      resetData();
    };
  }, [history, resetData]);

  useEffect(() => {
    shouldRefetch && refetchAppointment();
  }, [shouldRefetch, refetchAppointment]);

  return (
    <Card
      header={
        <div className="row align-items-center justify-content-between">
          <div className="col content-box-title">
            <div>
              <p className="text-capitalize mb-0 text-green-200">
                Manage{" "}
                {activeTab === "appointments" ? "Appointments" : "Visits"}
              </p>
            </div>
            <div className="collapse-icon"></div>
          </div>
          <div className="col-4">
            <form className="search-component d-flex justify-content-end">
              <ExportButton
                params={{ pageNumber, pageSize, searchKey }}
                component="Appointment"
              />
            </form>
          </div>
        </div>
      }
    >
      <Nav
        appearance="tabs"
        activeKey={activeTab}
        onSelect={setActiveTab}
        className="mb-3"
      >
        <Nav.Item eventKey="appointments">Appointments</Nav.Item>
        <Nav.Item eventKey="visits">Visits</Nav.Item>
      </Nav>

      {activeTab === "appointments" ? (
        <>
          <div className="mb-4 px-2 border-bottom border-1" id="filterPanel">
            {(status ||
              patientSKey ||
              caregiverSKey ||
              endDate ||
              startDate ||
              fromDate ||
              toDate) && (
              <div className="d-flex justify-content-end mb-1">
                <Button
                  name="Reset Filter Options"
                  className="btn-danger btn-sm"
                  onClick={resetData}
                >
                  Reset Filter Options
                </Button>
              </div>
            )}

            <Form onChange={handleChange} fluid>
              <div className="row">
                <div className="col-md-4">
                  <div className="row">
                    <SelectPickerInput
                      data={appointmentStatus}
                      name="appointmentStatus"
                      searchable={false}
                      value={status || predefinedStatus}
                      onChange={(data) =>
                        dispatch(ActionsAppointmentTable.setStatus(data))
                      }
                      renderEmpty={
                        <p className="text-muted p-3">No Data Available</p>
                      }
                    >
                      Filter By Appointment Status
                    </SelectPickerInput>
                    <SelectPickerInput
                      data={patientSelectItems}
                      name="patientSKey"
                      searchable={true}
                      value={patientSKey}
                      onChange={(data) =>
                        dispatch(ActionsAppointmentTable.setPatientSKey(data))
                      }
                      renderEmpty={
                        <p className="text-muted p-3">No Patients Available</p>
                      }
                    >
                      Filter By Patient
                    </SelectPickerInput>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="row">
                    <SelectPickerInput
                      data={caregiverSelectItems}
                      name="caregiverSKey"
                      searchable={true}
                      value={caregiverSKey}
                      onChange={(data) =>
                        dispatch(ActionsAppointmentTable.setCaregiverSKey(data))
                      }
                      renderEmpty={
                        <p className="text-muted p-3">
                          No Caregivers Available
                        </p>
                      }
                    >
                      Filter By Caregiver
                    </SelectPickerInput>
                    <CustomDatePicker
                      name="start"
                      action={ActionsAppointmentTable}
                      value={
                        startDate || fromDate
                          ? new Date(startDate || fromDate!)
                          : null
                      }
                    >
                      Appointment Start Date
                    </CustomDatePicker>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="row">
                    <CustomDatePicker
                      name="end"
                      action={ActionsAppointmentTable}
                      value={
                        endDate || toDate ? new Date(endDate || toDate!) : null
                      }
                      setDate={ActionsAppointmentTable.setEndDate}
                    >
                      Appointment End Date
                    </CustomDatePicker>
                  </div>
                </div>
              </div>
            </Form>
          </div>
          <AppointmentList
            data={appointmentData?.list}
            totalCount={appointmentData?.totalCount || 0}
            isValidating={isAppointmentDataFetching}
          />
        </>
      ) : (
        <VisitList companyId={StorageAuth.CompanyId!} />
      )}
    </Card>
  );
};
